/**
 * Dates et heures d'une ASSOCIATION sur le site public, dans SON fuseau
 * horaire (internationalisation, lot 2 — 2026-09-25).
 *
 * POURQUOI : tout s'affichait en heure de Paris. Pour L'Espoir de Sainte-Luce
 * (Martinique, UTC-4, 6 h de moins que Paris l'été), un gala à 19:00 heure
 * locale s'affichait « 01:00 » le lendemain. L'API publique porte désormais
 * le fuseau de l'association sur ses objets (`event.timezone`,
 * `plan.timezone`, page d'association) : l'heure affichée est l'heure
 * MURALE de l'association, quel que soit l'endroit d'où l'on regarde.
 * Fuseau absent (ancienne API) → Paris, le comportement d'avant.
 */
import { formatInZone, parseDbInstant, ORG_TIMEZONE } from "@donoor/utils/dates";

/**
 * Fuseau porté par un objet public de l'API (`event`, `plan`, organisation) —
 * « Europe/Paris » s'il est absent ou vide.
 */
export function fuseauDe(objet: unknown): string {
  const fuseau = (objet as { timezone?: unknown } | null | undefined)?.timezone;
  return typeof fuseau === "string" && fuseau ? fuseau : ORG_TIMEZONE;
}

/** Instant valide, ou `null`. Chaîne naïve de la base = UTC (parseDbInstant). */
export function instantAssociation(valeur: Date | string | null | undefined): Date | null {
  if (!valeur) return null;
  const d = parseDbInstant(valeur);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Affiche une date d'association dans son fuseau. « YYYY-MM-DD » (colonne
 * `date`, un jour sans heure) est affiché tel quel : aucun fuseau ne peut le
 * faire changer de jour. "" si vide ou illisible.
 */
export function formatDateAssociation(
  valeur: Date | string | null | undefined,
  fuseau: string | null | undefined,
  opts: Intl.DateTimeFormatOptions,
): string {
  if (!valeur) return "";
  if (typeof valeur === "string" && /^\d{4}-\d{2}-\d{2}$/.test(valeur.trim())) {
    return formatInZone(`${valeur.trim()}T12:00:00Z`, "UTC", opts);
  }
  const d = instantAssociation(valeur);
  return d ? formatInZone(d, fuseau || ORG_TIMEZONE, opts) : "";
}

/**
 * Instant ISO 8601 AVEC le décalage du fuseau de l'association
 * (« 2026-10-10T19:00:00-04:00 ») — pour les données structurées (JSON-LD
 * `startDate`) : Google lit l'heure locale ET le bon instant. "" si illisible.
 */
export function isoAvecDecalage(
  valeur: Date | string | null | undefined,
  fuseau: string | null | undefined,
): string {
  const d = instantAssociation(valeur);
  if (!d) return "";
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: fuseau || ORG_TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  }).formatToParts(d);
  const get = (t: Intl.DateTimeFormatPartTypes) => parts.find((p) => p.type === t)?.value ?? "00";
  const murale = Date.UTC(
    Number(get("year")),
    Number(get("month")) - 1,
    Number(get("day")),
    Number(get("hour")),
    Number(get("minute")),
    Number(get("second")),
  );
  // Décalage = heure murale − instant, à la minute (les secondes du fuseau
  // sont nulles pour tous les fuseaux actuels).
  const decalage = Math.round((murale - Math.floor(d.getTime() / 1000) * 1000) / 60000);
  const signe = decalage >= 0 ? "+" : "-";
  const abs = Math.abs(decalage);
  const hh = String(Math.floor(abs / 60)).padStart(2, "0");
  const mm = String(abs % 60).padStart(2, "0");
  return `${get("year")}-${get("month")}-${get("day")}T${get("hour")}:${get("minute")}:${get("second")}${signe}${hh}:${mm}`;
}

// ─── Ajout à l'agenda (Google Agenda, fichier .ics) ────────────────────────

/** Instant → « 20261010T230000Z » (UTC, format iCalendar / Google Agenda). */
export function dateAgendaUtc(d: Date): string {
  return d.toISOString().replace(/[-:]/g, "").replace(/\.\d{3}/, "");
}

/** Durée par défaut quand l'événement n'a pas d'heure de fin. */
const DUREE_PAR_DEFAUT_MS = 2 * 60 * 60 * 1000;

export interface EvenementAgenda {
  titre: string;
  /** Début (instant ; chaîne naïve de la base = UTC). */
  debut: Date | string;
  fin?: Date | string | null;
  lieu?: string;
  description?: string;
}

/**
 * Bornes UTC de l'événement pour l'agenda, ou `null` si le début est
 * illisible. Avant (bug 2026-09-25), `new Date("2026-10-10T23:00:00")` lisait
 * la chaîne naïve de la base dans le fuseau du NAVIGATEUR : l'événement
 * arrivait décalé dans l'agenda de TOUT le monde (de 2 h à Paris l'été).
 */
export function bornesAgenda(ev: EvenementAgenda): { debut: string; fin: string } | null {
  const debut = instantAssociation(ev.debut);
  if (!debut) return null;
  const fin = instantAssociation(ev.fin) ?? new Date(debut.getTime() + DUREE_PAR_DEFAUT_MS);
  return { debut: dateAgendaUtc(debut), fin: dateAgendaUtc(fin) };
}

/** Lien « Ajouter à Google Agenda ». "" si le début est illisible. */
export function lienGoogleAgenda(ev: EvenementAgenda): string {
  const bornes = bornesAgenda(ev);
  if (!bornes) return "";
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: ev.titre,
    dates: `${bornes.debut}/${bornes.fin}`,
    ...(ev.lieu ? { location: ev.lieu } : {}),
    ...(ev.description ? { details: ev.description } : {}),
  });
  return `https://calendar.google.com/calendar/render?${params}`;
}

/** Texte iCalendar : `\`, `;`, `,` et retours à la ligne échappés (RFC 5545 §3.3.11). */
function texteIcs(valeur: string): string {
  return valeur
    .replace(/\\/g, "\\\\")
    .replace(/;/g, "\\;")
    .replace(/,/g, "\\,")
    .replace(/\r?\n/g, "\\n");
}

/**
 * Contenu d'un fichier .ics (RFC 5545 : lignes en CRLF, UID et DTSTAMP
 * obligatoires). Les heures sont en UTC (`Z`) : l'agenda de chacun les
 * affiche dans son propre fuseau, au bon instant. "" si le début est
 * illisible.
 */
export function contenuIcs(ev: EvenementAgenda & { uid: string; maintenant?: Date }): string {
  const bornes = bornesAgenda(ev);
  if (!bornes) return "";
  return [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Donoor//Billetterie//FR",
    "CALSCALE:GREGORIAN",
    "BEGIN:VEVENT",
    `UID:${ev.uid}`,
    `DTSTAMP:${dateAgendaUtc(ev.maintenant ?? new Date())}`,
    `DTSTART:${bornes.debut}`,
    `DTEND:${bornes.fin}`,
    `SUMMARY:${texteIcs(ev.titre)}`,
    ...(ev.lieu ? [`LOCATION:${texteIcs(ev.lieu)}`] : []),
    ...(ev.description ? [`DESCRIPTION:${texteIcs(ev.description)}`] : []),
    "END:VEVENT",
    "END:VCALENDAR",
  ].join("\r\n");
}
